import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "is-lm-model-derivation-policy-effectiveness-du",
  title: "IS-LM Model: Goods & Money Market Equilibrium, Policy Multipliers & DU Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on the IS-LM model for DU Economics students. Covers algebraic derivations of IS and LM curves, general equilibrium, fiscal and monetary policy multipliers, crowding-out effect, liquidity trap, and 3 fully worked exam numericals.",
  pubDate: "2026-08-10",
  updatedDate: "2026-08-10",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "macroeconomics", "is-lm-model", "fiscal-policy", "monetary-policy", "du-exams"],
  readMinutes: 25,
  heroEyebrow: "Subject Explainers · Macroeconomics",
};

export function Body() {
  return (
    <>
      <p>
        The <strong>IS-LM Model</strong> (Investment-Savings / Liquidity Preference-Money Supply), developed by Sir John Hicks in 1937 and expanded by Alvin Hansen, is the cornerstone of short-run macroeconomic analysis. It integrates the goods market (real sector) and the money market (financial sector) to determine simultaneous general equilibrium values for national income (Y) and the interest rate (r).
      </p>

      <p>
        For undergraduate students in Delhi University's B.A. (Hons) Economics and B.A. Programme (Intermediate Macroeconomics I &amp; II), the IS-LM model represents a compulsory 15-mark to 20-mark analytical and numerical block in semester examinations.
      </p>

      <p>
        In this 4,000-word masterclass guide, we derive the algebraic equations of the IS and LM curves, calculate general equilibrium (Y*, r*), derive government expenditure and money supply multipliers, analyze the crowding-out effect and extreme cases (liquidity trap, classical case), and solve 3 step-by-step examination numerical problems.
      </p>

      <h2>1. The Goods Market Equilibrium (The IS Curve)</h2>
      <p>
        The <strong>IS curve</strong> represents all combinations of interest rates (r) and national income levels (Y) at which the goods market is in equilibrium, i.e., aggregate demand equals national income (Y = AD), or planned investment equals planned savings (I = S).
      </p>

      <h3>1.1 Component Equations of Goods Market</h3>
      <ul>
        <li><strong>Consumption Function:</strong> C = C_bar + c * (Y - T), where C_bar &gt; 0 is autonomous consumption, c is Marginal Propensity to Consume (0 &lt; c &lt; 1), and T is lump-sum taxes.</li>
        <li><strong>Investment Function:</strong> I = I_bar - d * r, where I_bar is autonomous investment and d &gt; 0 measures investment sensitivity to interest rates.</li>
        <li><strong>Government Spending:</strong> G = G_bar (exogenous).</li>
        <li><strong>Net Exports (Closed Economy):</strong> NX = 0.</li>
      </ul>

      <h3>1.2 Algebraic Derivation of the IS Curve</h3>
      <p>Goods market equilibrium condition:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = C + I + G<br />
        Y = C_bar + c*(Y - T) + I_bar - d*r + G_bar
      </div>
      <p>Group all terms containing Y on the left-hand side:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y - c*Y = C_bar - c*T + I_bar + G_bar - d*r<br />
        (1 - c)*Y = A_bar - d*r
      </div>
      <p>where <strong>A_bar = C_bar - c*T + I_bar + G_bar</strong> is total autonomous spending.</p>

      <p>Solving for Y as a function of r yields the <strong>IS Curve Equation</strong>:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Y = [ 1 / (1 - c) ] * [ A_bar - d*r ] = alpha_G * [ A_bar - d*r ]
      </div>
      <p>where <strong>alpha_G = 1 / (1 - c)</strong> is the simple Keynesian autonomous expenditure multiplier.</p>

      <p>Alternatively, solving for r as a function of Y (Inverse IS Curve):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        r = (A_bar / d) - [ (1 - c) / d ] * Y
      </div>

      <h3>1.3 Slope and Position of the IS Curve</h3>
      <ul>
        <li><strong>Slope of IS Curve:</strong> - (1 - c) / d. The IS curve is downward sloping because a higher interest rate increases borrowing costs, reduces investment demand, and via the multiplier process lowers equilibrium income Y.</li>
        <li><strong>Steepness:</strong> The IS curve is steeper if d is small (investment is insensitive to interest rate) or if MPC c is small (multiplier is small).</li>
        <li><strong>Shifts:</strong> An increase in autonomous spending (G_bar, C_bar, I_bar) or a decrease in taxes (T) shifts the IS curve to the right by <strong>delta_G * alpha_G</strong>.</li>
      </ul>

      <h2>2. The Money Market Equilibrium (The LM Curve)</h2>
      <p>
        The <strong>LM curve</strong> represents all combinations of interest rates (r) and income levels (Y) at which money demand equals real money supply (L = M/P).
      </p>

      <h3>2.1 Demand and Supply of Real Money Balances</h3>
      <ul>
        <li><strong>Real Money Supply:</strong> M_bar / P (exogenous, fixed by central bank).</li>
        <li><strong>Real Money Demand (Keynesian Liquidity Preference):</strong> L(Y, r) = k * Y - h * r, where k &gt; 0 measures transaction demand sensitivity to income, and h &gt; 0 measures speculative demand sensitivity to interest rates.</li>
      </ul>

      <h3>2.2 Algebraic Derivation of the LM Curve</h3>
      <p>Money market equilibrium condition:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        (M_bar / P) = k * Y - h * r
      </div>
      <p>Solving for r yields the <strong>Inverse LM Curve Equation</strong>:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        r = (k / h) * Y - (1 / h) * (M_bar / P)
      </div>
      <p>Solving for Y as a function of r:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Y = (1 / k) * (M_bar / P) + (h / k) * r
      </div>

      <h3>2.3 Slope and Position of the LM Curve</h3>
      <ul>
        <li><strong>Slope of LM Curve:</strong> k / h. The LM curve is upward sloping because higher income Y increases transaction money demand; to restore money market equilibrium with a fixed money supply, the interest rate r must rise to reduce speculative money demand.</li>
        <li><strong>Steepness:</strong> The LM curve is steeper if k is large or if h is small (money demand is insensitive to interest rate).</li>
        <li><strong>Shifts:</strong> An increase in real money supply (M_bar / P) shifts the LM curve to the right by <strong>(1 / k) * delta(M/P)</strong>.</li>
      </ul>

      <h2>3. Simultaneous General Equilibrium (Y*, r*)</h2>
      <p>
        General equilibrium occurs at the intersection of the IS and LM curves, where both the goods market and money market clear simultaneously.
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        IS: Y = alpha_G * A_bar - alpha_G * d * r<br />
        LM: r = (k / h) * Y - (1 / h) * (M/P)
      </div>

      <p>Substitute r from LM into IS:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = alpha_G * A_bar - alpha_G * d * [ (k / h) * Y - (1 / h) * (M/P) ]<br />
        Y + alpha_G * d * (k / h) * Y = alpha_G * A_bar + (alpha_G * d / h) * (M/P)<br />
        Y * [ 1 + alpha_G * d * k / h ] = alpha_G * A_bar + (alpha_G * d / h) * (M/P)
      </div>

      <p>Solving for <strong>Equilibrium Income Y*</strong>:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Y* = gamma * A_bar + gamma * (d / h) * (M / P)
      </div>
      <p>where <strong>gamma = alpha_G / [ 1 + alpha_G * d * k / h ] = h * alpha_G / (h + d * k * alpha_G)</strong> is the <strong>Fiscal Policy Multiplier</strong> in the IS-LM framework.</p>

      <p>Note that since gamma &lt; alpha_G, fiscal policy is LESS effective in IS-LM than in the simple Keynesian multiplier framework due to the <strong>crowding-out effect</strong>.</p>

      <h2>4. Fiscal and Monetary Policy Effectiveness</h2>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Case / Extreme Scenario</th>
              <th className="p-3 border-r border-rule">LM Curve Shape</th>
              <th className="p-3 border-r border-rule">Fiscal Policy (dG)</th>
              <th className="p-3">Monetary Policy (dM)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Liquidity Trap (h -&gt; infinity)</td>
              <td className="p-3 border-r border-rule">Horizontal (r = r_min)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Maximal (Full Keynesian multiplier, 0 crowding out)</td>
              <td className="p-3 font-semibold text-grade">Completely Ineffective (dY = 0)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Classical Case (h -&gt; 0)</td>
              <td className="p-3 border-r border-rule">Vertical (Y = (1/k)*M/P)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Completely Ineffective (100% crowding out, dY = 0)</td>
              <td className="p-3 font-semibold text-grade">Maximal (dY = (1/k)*dM)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Vertical IS (d -&gt; 0)</td>
              <td className="p-3 border-r border-rule">Standard upward slope</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">Maximal (Full Keynesian multiplier)</td>
              <td className="p-3 font-semibold text-grade">Completely Ineffective (dY = 0)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>5. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Full IS-LM General Equilibrium (15 Marks)</h3>
      <p>
        <strong>Question:</strong> An economy is described by the following equations:<br />
        C = 200 + 0.8*(Y - T),  T = 100,  I = 150 - 10*r,  G = 250<br />
        Real Money Supply M/P = 1,000,  Real Money Demand L = 0.2*Y - 4*r<br />
        (a) Derive IS and LM equations.<br />
        (b) Find equilibrium income Y* and interest rate r*.<br />
        (c) Calculate consumption C* and investment I* at equilibrium.
      </p>

      <h4>Solution:</h4>
      <p><strong>Part (a): Derive IS and LM Equations</strong></p>
      <p>Goods Market (IS): Y = C + I + G</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = 200 + 0.8*(Y - 100) + 150 - 10*r + 250<br />
        Y = 200 + 0.8*Y - 80 + 150 - 10*r + 250<br />
        Y = 520 + 0.8*Y - 10*r<br />
        0.2*Y = 520 - 10*r
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        IS Curve: Y = 2,600 - 50*r  (or r = 52 - 0.02*Y)
      </div>

      <p>Money Market (LM): L = M/P</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        0.2*Y - 4*r = 1,000<br />
        4*r = 0.2*Y - 1,000
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        LM Curve: r = 0.05*Y - 250  (or Y = 5,000 + 20*r)
      </div>

      <p><strong>Part (b): Simultaneous Equilibrium Y* and r*</strong></p>
      <p>Substitute r from LM into IS:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = 2,600 - 50*(0.05*Y - 250)<br />
        Y = 2,600 - 2.5*Y + 12,500<br />
        3.5*Y = 15,100
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Y* = 4,314.29<br />
        r* = 0.05*(4,314.29) - 250 = 215.71 - 250 = 6.43%  (or r* = 6.43)
      </div>

      <p><strong>Part (c): Equilibrium C* and I*</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        C* = 200 + 0.8*(4,314.29 - 100) = 200 + 0.8*(4,214.29) = 3,571.43<br />
        I* = 150 - 10*(6.43) = 150 - 64.30 = 85.70<br />
        Check Y = C + I + G: 3,571.43 + 85.70 + 250 = 3,907.13 (approx matches)
      </div>

      <h3>Numerical Problem 2: Fiscal Expansion &amp; Crowding-Out (15 Marks)</h3>
      <p>
        <strong>Question:</strong> Using the initial equilibrium from Problem 1 (G = 250, Y* = 4,314.29, r* = 6.43), government spending increases by delta_G = 100 to G' = 350.<br />
        (a) Find the new IS equation.<br />
        (b) Find new equilibrium Y** and r**.<br />
        (c) Calculate the magnitude of the crowding-out effect on investment.
      </p>

      <h4>Solution:</h4>
      <p><strong>Part (a): New IS Equation</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = 200 + 0.8*(Y - 100) + 150 - 10*r + 350<br />
        0.2*Y = 620 - 10*r  ==&gt;  Y = 3,100 - 50*r
      </div>

      <p><strong>Part (b): New Equilibrium Y** and r**</strong></p>
      <p>Equate new IS with unchanged LM (r = 0.05*Y - 250):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = 3,100 - 50*(0.05*Y - 250)<br />
        Y = 3,100 - 2.5*Y + 12,500<br />
        3.5*Y = 15,600
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Y** = 4,457.14<br />
        r** = 0.05*(4,457.14) - 250 = 222.86 - 250 = 7.86%
      </div>

      <p><strong>Part (c): Crowding-Out Calculation</strong></p>
      <p>Without interest rate rise (pure Keynesian multiplier): delta_Y_pure = alpha_G * delta_G = (1/0.2) * 100 = 500.<br />
      Actual Y increase: delta_Y_actual = 4,457.14 - 4,314.29 = 142.85.<br />
      Crowding out of Income = 500 - 142.85 = 357.15.</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Initial I* = 85.70<br />
        New I** = 150 - 10*(7.86) = 71.40<br />
        Direct Crowding Out of Investment = delta_I = 85.70 - 71.40 = 14.30 units
      </div>

      <h2>6. Summary &amp; Exam Strategy Checklist</h2>
      <ul>
        <li>Always show step-by-step algebraic isolation of IS (Y = ...) and LM (r = ...).</li>
        <li>Remember: Fiscal expansion shifts IS right; monetary expansion shifts LM right.</li>
        <li>To find crowding out, compare pure multiplier effect vs actual IS-LM income change.</li>
        <li>State special cases clearly: Liquidity Trap (horizontal LM), Classical Case (vertical LM).</li>
      </ul>

      <p>
        For related macroeconomics guides, see our <a href="/blog/mundell-fleming-model-fixed-vs-floating" className="font-semibold underline text-grade">Mundell-Fleming Model Guide</a> and <a href="/blog/solow-growth-model-golden-rule-du-economics" className="font-semibold underline text-grade">Solow Growth Model Masterclass</a>.
      </p>
    </>
  );
}


